import { useMemo } from "react";
import type { EventHistoryHostContext, HostServices } from "@amzn/ytyard360-event-history";
import { ENDPOINTS } from "@/app/app-config";
import { REDIRECT_LINKS, ROOT_URLS, ROUTES } from "@/app/app-constants";
import { getSecurityToken } from "@/federation/getSecurityToken";
import { useHostSelector } from "@/federation/useHostSelector";
import { store } from "@/store";
import { convertRealmToRegion } from "@/features/yard360/helpers/common/convertRealmToRegionLogic";
import { pendoService } from "@/services/pendo/Pendo";
import rumService, { RUM_EVENT_TYPES, type RumEventType } from "@/utils/awsRUM/awsRUM";

interface AuthRealmDomain {
  realm: string;
  domain: string;
}

const getServiceUrl = (service: string): string => {
  const { authInfo } = store.getState() as { authInfo?: Partial<AuthRealmDomain> };
  const realm = authInfo?.realm;
  const domain = authInfo?.domain;
  if (!realm || !domain) {
    throw new Error("eventHistoryHostServices: authInfo realm/domain not initialized");
  }
  return ENDPOINTS[realm][domain][service];
};

const useHostContext = (): EventHistoryHostContext => {
  const realm = useHostSelector((state) => state.auth.realm);
  const domain = useHostSelector((state) => state.auth.domain);
  const activeBuildingCode = useHostSelector((state) => state.auth.activeBuildingCode);

  return useMemo(
    () => ({
      activeBuildingCode,
      realm,
      fmcBaseUrl: ROOT_URLS[realm]?.[domain] ?? "",
      rttBaseUrl: REDIRECT_LINKS[realm]?.[domain]?.RELAY_TRACK_TRACE ?? "",
      rttRegionId: convertRealmToRegion(realm) ?? "",
    }),
    [activeBuildingCode, domain, realm]
  );
};

export const eventHistoryHostServices: HostServices = {
  getSecurityToken,
  getYardConsoleEndpoint: () => getServiceUrl("YardConsoleService"),
  getYard360Endpoint: () => getServiceUrl("Yard360Service"),
  useHostContext,
  routes: {
    eventHistory: ROUTES.EVENT_REPORT,
    eventHistoryDetail: ROUTES.EVENT_REPORT_DETAIL,
    yardControl: ROUTES.RYC,
    autoDock: ROUTES.RYTE_TRACKER,
    hostlerPool: ROUTES.HOSTLER_POOL,
  },
  telemetry: {
    trackPendoEvent: (eventName: string, data?: Record<string, unknown>) =>
      pendoService.trackEvent(eventName, data),
    trackRumCustomEvent: (event: {
      type: string;
      message: string;
      data?: Record<string, unknown>;
    }) =>
      rumService.customEvent({
        type: (Object.values(RUM_EVENT_TYPES) as string[]).includes(event.type)
          ? (event.type as RumEventType)
          : RUM_EVENT_TYPES.USER_ACTION,
        message: event.message,
        data: event.data,
      }),
  },
};
